import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import type {
  CreateSharedMember,
  Household,
  HouseholdMember,
  HouseholdSettings,
} from '@chore-project/shared';

export interface UpdateMemberPayload {
  memberName: string;
  avatarColor: string;
  avatarIcon: string;
}

export function useHouseholds() {
  return useQuery<Household[]>({
    queryKey: ['households'],
    queryFn: async () => {
      const { data } = await apiClient.get<Household[]>('/households');
      return data;
    },
  });
}

export function useHousehold(householdId: string) {
  return useQuery<Household>({
    queryKey: ['households', householdId],
    queryFn: async () => {
      const { data } = await apiClient.get<Household>(`/households/${householdId}`);
      return data;
    },
    enabled: !!householdId,
  });
}

export function useHouseholdMembers(householdId: string) {
  return useQuery<HouseholdMember[]>({
    queryKey: ['households', householdId, 'members'],
    queryFn: async () => {
      const { data } = await apiClient.get<HouseholdMember[]>(
        `/households/${householdId}/members`,
      );
      return data;
    },
    enabled: !!householdId,
  });
}

/**
 * Members of several households, merged into one array. Each HouseholdMember
 * carries its householdId, so callers can still resolve names per household.
 */
export function useAllHouseholdMembers(householdIds: string[]) {
  const results = useQueries({
    queries: householdIds.map((id) => ({
      queryKey: ['households', id, 'members'],
      queryFn: async () => {
        const { data } = await apiClient.get<HouseholdMember[]>(`/households/${id}/members`);
        return data;
      },
      enabled: !!id,
    })),
  });
  const members = results.flatMap((r) => r.data ?? []);
  const isLoading = results.some((r) => r.isLoading);
  const isError = results.some((r) => r.isError);
  return { data: members, isLoading, isError };
}

/**
 * Settings for several households, keyed by householdId — for pages that show
 * chores from every household and need per-household behaviour (e.g. whether
 * skipping is allowed). Missing entries mean "still loading"; treat their
 * boolean settings as the defaults.
 */
export function useAllHouseholdSettings(householdIds: string[]) {
  const results = useQueries({
    queries: householdIds.map((id) => ({
      queryKey: ['households', id, 'settings'],
      queryFn: async () => {
        const { data } = await apiClient.get<HouseholdSettings>(`/households/${id}/settings`);
        return data;
      },
      enabled: !!id,
    })),
  });
  const byId = new Map<string, HouseholdSettings>();
  results.forEach((r, i) => {
    const id = householdIds[i];
    if (id && r.data) byId.set(id, r.data);
  });
  const isLoading = results.some((r) => r.isLoading);
  return { data: byId, isLoading };
}

export function useCreateHousehold() {
  const queryClient = useQueryClient();
  return useMutation<Household, Error, { name: string }>({
    mutationFn: async (dto) => {
      const { data } = await apiClient.post<Household>('/households', dto);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['households'] }),
  });
}

export function useInviteMember(householdId: string) {
  const queryClient = useQueryClient();
  return useMutation<{ token: string; email: string }, Error, { email: string }>({
    mutationFn: async (dto) => {
      const { data } = await apiClient.post<{ token: string; email: string }>(
        `/households/${householdId}/invite`,
        dto,
      );
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['households', householdId, 'invitations'] }),
  });
}

export function useCreateSharedMember(householdId: string) {
  const queryClient = useQueryClient();
  return useMutation<HouseholdMember, Error, CreateSharedMember>({
    mutationFn: async (dto) => {
      const { data } = await apiClient.post<HouseholdMember>(
        `/households/${householdId}/shared-members`,
        dto,
      );
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['households', householdId, 'members'] }),
  });
}

export function useUpdateSharedMember(householdId: string) {
  const queryClient = useQueryClient();
  return useMutation<HouseholdMember, Error, { userId: string; sharedPool: boolean }>({
    mutationFn: async ({ userId, sharedPool }) => {
      const { data } = await apiClient.patch<HouseholdMember>(
        `/households/${householdId}/shared-members/${userId}`,
        { sharedPool },
      );
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['households', householdId, 'members'] }),
  });
}

export function useDeleteSharedMember(householdId: string) {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { userId: string }>({
    mutationFn: async ({ userId }) => {
      await apiClient.delete(`/households/${householdId}/shared-members/${userId}`);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['households', householdId, 'members'] });
      void queryClient.invalidateQueries({ queryKey: ['points'] });
    },
  });
}

export function useUpdateMember(householdId: string) {
  const queryClient = useQueryClient();
  return useMutation<HouseholdMember, Error, UpdateMemberPayload>({
    mutationFn: async (dto) => {
      const { data } = await apiClient.patch<HouseholdMember>(
        `/households/${householdId}/members/me`,
        dto,
      );
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['households', householdId, 'members'] }),
  });
}

export function useHouseholdSettings(householdId: string) {
  return useQuery<HouseholdSettings>({
    queryKey: ['households', householdId, 'settings'],
    queryFn: async () => {
      const { data } = await apiClient.get<HouseholdSettings>(
        `/households/${householdId}/settings`,
      );
      return data;
    },
    enabled: !!householdId,
  });
}

export function useUpdateHouseholdSettings(householdId: string) {
  const queryClient = useQueryClient();
  return useMutation<HouseholdSettings, Error, Partial<HouseholdSettings>>({
    mutationFn: async (dto) => {
      const { data } = await apiClient.patch<HouseholdSettings>(
        `/households/${householdId}/settings`,
        dto,
      );
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ['households', householdId, 'settings'] }),
  });
}
